<template>
  <div class="schedule-advanced-stats" v-if="availableSchedule">
    <div class="dashboard-panels">
      <div class="dashboard-panels--tabs">
        <div>
          <!-- Header and Selectors -->
          <div class="d-flex justify-space-between align-center">
            <SectionHeader class="pb-0" first="Estadísticas" second="N2/Cuadrantes" />
          </div>
          <div class="dashboard-toolbar--selector pb-3 d-flex mt-4">
            <div class="dashboard-toolbar--field-group mr-5">
              <div class="dashboard-toolbar--field-group__label work-center">
                <v-icon small>mdi-office-building-marker</v-icon>
                Centro de trabajo:
              </div>
              <div class="dashboard-toolbar--field-group__value">
                <div class="dashboard-toolbar--field-group__value__text" v-if="workcenterId !== null">
                  <v-select style="min-width: 400px;" v-model="selectedWorkcenterId" :items="workcenters"
                    :placeholder="workcenterName ? workcenterName : 'Seleccionar Centro de Trabajo'" outlined dense
                    hide-details @change="loadStats()">
                  </v-select>
                </div>
              </div>
            </div>
            <div class="dashboard-toolbar--field-group">
              <div class="dashboard-toolbar--field-group__label work-center">
                <v-icon small>mdi-calendar</v-icon>
                Año:
              </div>
              <div class="dashboard-toolbar--field-group__value">
                <div class="dashboard-toolbar--field-group__value__text">
                  <v-select v-model="selectedYear" :items="years"
                    :placeholder="selectedYear ? selectedYear.toString() : 'Seleccionar Año'" outlined dense
                    hide-details @change="loadStats()"></v-select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <v-divider class="mb-4"></v-divider>

        <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 300px;">
          <v-progress-circular indeterminate color="primary" size="56"></v-progress-circular>
        </div>

        <div v-else-if="!statsData" class="d-flex flex-column align-center justify-center" style="min-height: 300px;">
          <v-icon size="72" class="text--disabled">mdi-chart-areaspline</v-icon>
          <p class="text--secondary mt-3">Selecciona un centro y año para ver las estadísticas de cuadrantes
          </p>
        </div>

        <div v-else class="px-4">
          <v-row class="mb-4">
            <!-- Schedule KPIs Horizontal Row -->
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100"
                style="border-left: 4px solid #00B0FF !important;">
                <v-icon large color="#00B0FF" class="mr-3">mdi-weather-sunny</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Turnos Mañana</div>
                  <div class="text-h5 font-weight-bold">{{ statsData.morningCount }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100"
                style="border-left: 4px solid #FFEB3B !important;">
                <v-icon large color="#FFEB3B" class="mr-3">mdi-weather-sunset</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Turnos Tarde</div>
                  <div class="text-h5 font-weight-bold">{{ statsData.eveningCount }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100"
                style="border-left: 4px solid #FF4081 !important;">
                <v-icon large color="#FF4081" class="mr-3">mdi-weather-night</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Turnos Noche</div>
                  <div class="text-h5 font-weight-bold">{{ statsData.nightCount }}</div>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-card class="elevation-1 pa-3 rounded-lg d-flex align-center h-100"
                style="border-left: 4px solid #03fc73 !important;">
                <v-icon large color="#03fc73" class="mr-3">mdi-account-remove</v-icon>
                <div>
                  <div class="text-subtitle-2 text--secondary">Absentismos</div>
                  <div class="text-h5 font-weight-bold">{{ statsData.absenceCount }}</div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <!-- Schedule Chart Full Width -->
            <v-col cols="12" md="12">
              <v-card class="elevation-1 pa-4 h-100 rounded-lg">
                <h3 class="text-subtitle-1 font-weight-bold mb-4">
                  <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
                  Evolución Anual ({{ selectedYear }})
                </h3>
                <div style="height: 300px; position: relative;">
                  <ScheduleChart ref="scheduleChart" :chart-data="temporalData" />
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Resumen por meses -->
          <v-card elevation="1" class="mt-6 mb-6">
            <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
              <v-icon color="primary" class="mr-2">mdi-calendar-multiselect</v-icon>
              Desglose Mensual
            </v-card-title>
            <v-divider></v-divider>
            <v-simple-table>
              <thead>
                <tr>
                  <th>Mes</th>
                  <th>Mañanas</th>
                  <th>Tardes</th>
                  <th>Noches</th>
                  <th>Absentismos</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in temporalData" :key="d.monthNumber">
                  <td>
                    <div class="font-weight-medium text-capitalize">{{ d.monthName.toLowerCase() }}</div>
                  </td>
                  <td class="font-weight-bold">{{ d.morningCount }}</td>
                  <td class="font-weight-bold">{{ d.eveningCount }}</td>
                  <td class="font-weight-bold">{{ d.nightCount }}</td>
                  <td class="font-weight-bold" style="color: #03fc73;">{{ d.absenceCount }}</td>
                </tr>
                <tr v-if="temporalData.length === 0">
                  <td colspan="5" class="text-center text--secondary py-4">No hay datos en el periodo.</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-cycle
import scheduleService from '@/services/schedule.service';
import SectionHeader from '@/components/Headers/SectionHeader.vue';
import workcenterService from '@/services/workcenter.service';
import ScheduleChart from '@/components/Charts/ScheduleChart.vue';

export default {
  name: 'schedule-advanced-stats',
  components: {
    SectionHeader,
    ScheduleChart,
  },
  data() {
    return {
      loading: false,

      workcenterId: null,
      workcenterName: null,

      years: [2025, 2026],
      currentYear: new Date().getFullYear(),
      selectedYear: null,

      workcenters: null,
      selectedWorkcenterId: null,

      statsData: null,
      temporalData: [],
    };
  },
  computed: {
    availableSchedule() {
      return this.$store.state.availableSchedule !== 'false' && this.$store.state.availableSchedule !== false;
    },
    avgIncidencesPerMonth() {
      if (!this.statsData || this.statsData.totalIncidences === 0) return '0.0';
      return (this.statsData.totalIncidences / 12).toFixed(1);
    },
  },
  created() {
    this.getWorkcenters();
  },
  mounted() {
    if (this.$store.state.projectId) {
      this.workcenterId = this.$store.state.projectId;
      this.workcenterName = this.$store.state.project;
      this.selectedWorkcenterId = this.workcenterId;
    }
    this.selectedYear = this.currentYear;
    this.loadStats();
  },
  methods: {
    getWorkcenters() {
      workcenterService.getAllWorkcenters().then((response) => {
        this.workcenters = response.content.map((ele) => ({
          value: ele.id,
          text: ele.name,
        }));
      });
    },
    async loadStats() {
      if (!this.selectedWorkcenterId || !this.selectedYear) return;
      this.loading = true;
      this.statsData = null;
      this.temporalData = [];

      try {
        // 1. Get all workers for this workcenter
        const workersResponse = await workcenterService.getWorkers({
          size: 999,
          workCenter: this.selectedWorkcenterId,
        });

        const workers = workersResponse.content || [];
        if (workers.length === 0) {
          this.statsData = {
            morningCount: 0,
            eveningCount: 0,
            nightCount: 0,
            absenceCount: 0,
          };
          this.loading = false;
          return;
        }

        // 2. Fetch days for all workers concurrently
        const dateFrom = `${this.selectedYear}-01-01`;
        const dateTo = `${this.selectedYear}-12-31`;

        const schedulesPromises = workers.map(
          (worker) => scheduleService.getDaysInRange(worker.id, dateFrom, dateTo),
        );

        const allSchedules = await Promise.all(schedulesPromises);

        let globalMorning = 0;
        let globalEvening = 0;
        let globalNight = 0;
        let globalAbsence = 0;

        // Initialize temporal grouped by month for all 12 months
        const monthsData = {};
        const monthNames = [
          'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
          'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE',
        ];

        for (let i = 0; i < 12; i += 1) {
          const monthNumber = i + 1;
          monthsData[monthNumber] = {
            monthNumber,
            monthName: monthNames[i],
            morningCount: 0,
            eveningCount: 0,
            nightCount: 0,
            absenceCount: 0,
          };
        }

        // 3. Aggregate types across all workers and all days
        allSchedules.forEach((workerDays) => {
          if (!workerDays) return;
          workerDays.forEach((dayEntry) => {
            if (!dayEntry.type) return;
            const monthNumber = new Date(dayEntry.date).getMonth() + 1;

            if (dayEntry.type === 'MORNING') {
              globalMorning += 1;
              monthsData[monthNumber].morningCount += 1;
            } else if (dayEntry.type === 'EVENING') {
              globalEvening += 1;
              monthsData[monthNumber].eveningCount += 1;
            } else if (dayEntry.type === 'NIGHT') {
              globalNight += 1;
              monthsData[monthNumber].nightCount += 1;
            } else if (dayEntry.type === 'ABSENCE') {
              globalAbsence += 1;
              monthsData[monthNumber].absenceCount += 1;
            }
          });
        });

        this.statsData = {
          morningCount: globalMorning,
          eveningCount: globalEvening,
          nightCount: globalNight,
          absenceCount: globalAbsence,
        };

        // Convert the map to an array and sort by month
        this.temporalData = Object.values(monthsData).sort((a, b) => a.monthNumber - b.monthNumber);

        this.$nextTick(() => {
          if (this.$refs.scheduleChart) {
            this.$refs.scheduleChart.updateChartData(this.temporalData);
          }
        });
      } catch (e) {
        console.error('Error fetching Schedule stats:', e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.schedule-advanced-stats {
  width: 100%;
}

.dashboard-toolbar--selector {
  display: flex;
  align-items: center;
}

.dashboard-toolbar--field-group {
  display: flex;
  flex-direction: column;
}

.dashboard-toolbar--field-group__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #666;
  margin-bottom: 4px;
}
</style>
